import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ApiError, apiClient, type TaskCard } from "../api/client";

export function ExecutionPage() {
  const { taskCardCode = "" } = useParams();
  const navigate = useNavigate();
  const [taskCard, setTaskCard] = useState<TaskCard | null>(null);
  const [scopeType, setScopeType] = useState("store");
  const [scopeId, setScopeId] = useState("");
  const [input, setInput] = useState(() => defaultInput(taskCardCode));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let current = true;
    apiClient
      .listTaskCards()
      .then((cards) => {
        if (!current) return;
        const selected = cards.find((item) => item.code === taskCardCode) ?? null;
        setTaskCard(selected);
        setScopeType(selected?.resource_scope_types[0] ?? "store");
      })
      .catch((caught: unknown) => current && setError(errorMessage(caught)))
      .finally(() => current && setLoading(false));
    return () => {
      current = false;
    };
  }, [taskCardCode]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    if (!taskCard) return;
    let inputPayload: Record<string, unknown>;
    try {
      const parsed: unknown = JSON.parse(input);
      if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) throw new Error();
      inputPayload = parsed as Record<string, unknown>;
    } catch {
      setError("任务输入必须是一个有效的 JSON 对象。");
      return;
    }
    setSubmitting(true);
    try {
      const run = await apiClient.startTaskCard({
        task_card: taskCard.code,
        scope_type: scopeType,
        scope_id: scopeId.trim(),
        input_payload: inputPayload,
      });
      navigate(`/runs/${run.id}`);
    } catch (caught) {
      setError(errorMessage(caught));
      setSubmitting(false);
    }
  }

  if (loading) {
    return <div className="route-loading">正在加载已发布任务卡…</div>;
  }
  if (!taskCard) {
    return (
      <section className="empty-page">
        <span className="eyebrow">执行目标不可用</span>
        <h1>任务卡未发布或已停用</h1>
        {error && <p role="alert">{error}</p>}
        <Link to="/">返回任务中心</Link>
      </section>
    );
  }

  return (
    <div className="page-stack execution-page">
      <header className="flow-header">
        <div>
          <span className="eyebrow">{modeLabel(taskCard.execution_mode)} · 显式选择</span>
          <h1>{taskCard.display_name}</h1>
          <p>{taskCard.description}</p>
        </div>
        <span className="phase-badge">{taskCard.target}</span>
      </header>
      <section className="flow-panel" aria-labelledby="execution-input-title">
        <div className="panel-heading">
          <div>
            <span className="eyebrow">调用者当前授权</span>
            <h2 id="execution-input-title">配置任务输入</h2>
          </div>
        </div>
        <form className="execution-form" onSubmit={submit}>
          <label htmlFor="execution-scope-type">资源类型</label>
          <select
            id="execution-scope-type"
            onChange={(event) => setScopeType(event.target.value)}
            value={scopeType}
          >
            {taskCard.resource_scope_types.map((item) => (
              <option key={item} value={item}>
                {scopeLabel(item)}
              </option>
            ))}
          </select>
          <label htmlFor="execution-scope-id">资源范围 ID</label>
          <input
            id="execution-scope-id"
            onChange={(event) => setScopeId(event.target.value)}
            placeholder="当前账号已获授权的 UUID"
            required
            value={scopeId}
          />
          <label htmlFor="execution-input">结构化任务输入</label>
          <textarea
            aria-describedby="execution-input-help"
            id="execution-input"
            onChange={(event) => setInput(event.target.value)}
            rows={10}
            value={input}
          />
          <small id="execution-input-help">
            输入会与任务卡版本、执行模式和资源范围一起固化，运行后不可改写。
          </small>
          {error && (
            <div className="alert error" role="alert">
              {error}
            </div>
          )}
          <div className="auth-actions">
            <button disabled={submitting || !scopeId.trim()} type="submit">
              {submitting ? "正在创建运行…" : `启动${modeLabel(taskCard.execution_mode)}`}
            </button>
            <Link className="secondary-button" to="/">
              返回任务中心
            </Link>
          </div>
        </form>
      </section>
      <details className="schema-panel">
        <summary>查看固定输入 Schema</summary>
        <pre>{JSON.stringify(taskCard.input_schema, null, 2)}</pre>
      </details>
    </div>
  );
}

function defaultInput(taskCard: string): string {
  const values: Record<string, Record<string, unknown>> = {
    "amazon-report-validate": { report_set_id: "" },
    "amazon-operations-open-task": { report_set_id: "", operations: ["weekly_report"] },
    "supply-chain-open-task": { request: "分析当前范围内的补货风险" },
    "image-creative-open-task": { request: "在品牌与权利约束内改进商品图片方案" },
  };
  return JSON.stringify(values[taskCard] ?? { request: "" }, null, 2);
}

function modeLabel(mode: TaskCard["execution_mode"]): string {
  return { skill: "直接 Skill", workflow: "固定 Workflow", agent: "领域 Agent" }[mode];
}

function scopeLabel(scope: string): string {
  return (
    {
      department: "部门",
      store: "店铺",
      brand: "品牌",
      site: "站点",
      dataset: "数据集",
    }[scope] ?? scope
  );
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.status === 404 ? "任务或资源范围不存在，或当前账号无权访问。" : error.message;
  }
  return "暂时无法创建运行，请稍后重试。";
}
